import React, { useState } from "react";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import { User } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { image } from "../../../../datos";
import { getUploadUrl } from "../../../../utils";
import { initialValues, validationSchema } from "./UserForm.form";

const userController = new User();

export function UserForm(props) {
  const { close, onReload, user } = props;
  const { accessToken } = useAuth();
  const [serverError, setServerError] = useState("");

  const formik = useFormik({
    initialValues: initialValues(user),
    validationSchema: validationSchema(user),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        setServerError("");
        if (!user) {
          await userController.createUser(accessToken, formValue);
        } else {
          await userController.updateUser(accessToken, user.id, formValue);
        }
        onReload();
        close();
      } catch (error) {
        console.error(error);
        setServerError(error.message || "Ha ocurrido un error al guardar el usuario");
      }
    },
  });

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      formik.setFieldValue("avatar", URL.createObjectURL(file));
      formik.setFieldValue("fileAvatar", file);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
    },
    maxFiles: 1,
    multiple: false,
    onDrop,
  });

  const getAvatar = () => {
    if (formik.values.fileAvatar) {
      return formik.values.avatar;
    } else if (formik.values.avatar) {
      return getUploadUrl(formik.values.avatar);
    }
    return image.noAvatar;
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={formik.handleSubmit}>
      {serverError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium">
          {serverError}
        </div>
      )}

      {/* Upload Avatar */}
      <div className="flex justify-center">
        <div 
          {...getRootProps()} 
          className={`relative w-24 h-24 rounded-full overflow-hidden border-4 cursor-pointer transition-all duration-300 ${isDragActive ? 'border-gray-900 scale-105' : 'border-gray-100 hover:border-gray-300'}`}
        >
          <input {...getInputProps()} />
          <img src={getAvatar()} alt="Avatar" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Nombre completo</label>
          <input
            name="name"
            placeholder="Ej. Juan Pérez"
            onChange={formik.handleChange}
            value={formik.values.name}
            className={`w-full px-4 py-3 bg-gray-50 border ${formik.errors.name ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none transition-all`}
          />
          {formik.errors.name && <span className="text-red-500 text-xs mt-1 block">{formik.errors.name}</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Correo electrónico</label>
          <input
            name="email"
            type="email"
            placeholder="correo@catelli.net"
            onChange={formik.handleChange}
            value={formik.values.email}
            className={`w-full px-4 py-3 bg-gray-50 border ${formik.errors.email ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none transition-all`}
          />
          {formik.errors.email && <span className="text-red-500 text-xs mt-1 block">{formik.errors.email}</span>}
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Rol</label>
          <select
            name="role"
            onChange={formik.handleChange}
            value={formik.values.role}
            className={`w-full px-4 py-3 bg-gray-50 border ${formik.errors.role ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none transition-all`}
          >
            <option value="" disabled>Selecciona un rol</option>
            {roleOptions.map(opt => <option key={opt.key} value={opt.value}>{opt.text}</option>)}
          </select>
          {formik.errors.role && <span className="text-red-500 text-xs mt-1 block">{formik.errors.role}</span>}
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Contraseña</label>
        <input
          type="password"
          name="password"
          placeholder={user ? "Dejar en blanco para no cambiar" : "••••••••"}
          onChange={formik.handleChange}
          value={formik.values.password}
          className={`w-full px-4 py-3 bg-gray-50 border ${formik.errors.password ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none transition-all`}
        />
        {formik.errors.password && <span className="text-red-500 text-xs mt-1 block">{formik.errors.password}</span>}
      </div>

      <button
        type="submit"
        disabled={formik.isSubmitting}
        className="w-full btn btn-primary py-4 rounded-xl font-bold mt-4 disabled:opacity-50"
      >
        {formik.isSubmitting ? "Guardando..." : (user ? "Actualizar usuario" : "Crear usuario")}
      </button>
    </form>
  );
}

const roleOptions = [
  {
    key: "user",
    text: "Usuario",
    value: "user",
  },
  {
    key: "consulta",
    text: "Consultas",
    value: "consulta",
  },
  {
    key: "admin",
    text: "Administrador",
    value: "admin",
  },
];
